import { cn } from '@repo/tailwind-config/tailwind.functions'
import { FileTypeEnum } from '@repo/toolkit/types'
import { TeacherIcon } from '@repo/ui/icons'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'

import { deleteCenterTeflCostsInvoice } from '../../../../api/centers/center-tefl-costs/delete-center-tefl-costs-invoice'
import { getCenterTeflCosts } from '../../../../api/centers/center-tefl-costs/get-center-tefl-costs'
import { updateCenterTeflCosts } from '../../../../api/centers/center-tefl-costs/update-center-tefl-costs'
import { uploadCenterTeflCostsInvoice } from '../../../../api/centers/center-tefl-costs/upload-center-tefl-costs-invoice'
import { getCenter } from '../../../../api/centers/get-center'
import { useObserver } from '../../../../lib/contexts/observer'
import useAuth from '../../../../lib/hooks/use-auth'
import { useDataProps } from '../../../../lib/hooks/use-data-props'
import useTranslate from '../../../../lib/hooks/use-translate'
import { Center } from '../../../../lib/types/models/center/center'
import {
  CenterTeflCost,
  CenterTeflCostInput,
  CenterTeflCostInvoiceInput
} from '../../../../lib/types/models/center/center-tefl-cost'
import { DataProps } from '../../../../lib/types/utils'
import { showAxiosError } from '../../../../lib/utils/functions/misc.functions'
import EditableTable, { EditableTableColumn } from '../../../elements/editable-table'
import { useCenterCosts } from './center.hooks'
import {
  centerCostsTableColorMapping,
  CenterCostsTableFooter,
  CenterCostsTableHeader,
  CenterTableSkeleton,
  InvoiceButton,
  UploadInvoicesButton,
  userInvoiceColorMapping,
  userUploadInvoiceColorMapping
} from './center.ui'
import {
  CenterCostsTableProps,
  centerTeflCostsEditableColumns,
  getCenterTeflCostsColumns,
  mapCenterTeflCostToInput
} from './utils'

export function CenterTeflCostsTable(props: DataProps<Center, CenterCostsTableProps>) {
  const { mode, costType, currency } = props
  const { t } = useTranslate()
  const { user } = useAuth()
  const [editable, setEditable] = useState(false)
  const [teflCosts, setTeflCosts] = useState<CenterTeflCostInput[]>()

  const {
    id: centerId,
    data,
    refetch: refetchCenter
  } = useDataProps(props, (id) => getCenter(id, 'FULL'))

  const { centerCostsView, lock, unlock, isPending } = useCenterCosts({
    centerId,
    costType,
    table: 'tefl'
  })

  const { data: centerTeflCosts, refetch } = useQuery({
    queryKey: ['center-tefl-costs', centerId],
    queryFn: () => getCenterTeflCosts(centerId)
  })

  useObserver({
    key: 'center-tefl-costs-table',
    update: refetch
  })

  const { mutateAsync } = useMutation({
    mutationFn: (data: CenterTeflCostInput[]) => updateCenterTeflCosts(centerId, data),
    onError: showAxiosError,
    onSuccess: () => {
      refetch()
      centerCostsView.refetch()
      setTeflCosts(undefined)
    }
  })

  const {
    mutate: uploadCenterTeflCostsInvoicesMutation,
    isPending: isUploadingCenterTeflCostsInvoicesPending
  } = useMutation({
    mutationFn: (data: { teflCostId: number; data: CenterTeflCostInvoiceInput }) =>
      uploadCenterTeflCostsInvoice(centerId, data.teflCostId, data.data),
    onError: showAxiosError,
    onSuccess: () => refetch()
  })

  const {
    mutate: deleteCenterTeflCostsInvoiceMutation,
    isPending: isDeletingCenterTeflCostsInvoicePending
  } = useMutation({
    mutationFn: (data: { teflCostId: number; invoiceId: number }) =>
      deleteCenterTeflCostsInvoice(centerId, data.teflCostId, data.invoiceId),
    onError: showAxiosError,
    onSuccess: () => refetch()
  })

  const isDisabled =
    isDeletingCenterTeflCostsInvoicePending ||
    isUploadingCenterTeflCostsInvoicesPending ||
    isPending
  const displayedTeflCosts = useMemo(
    () => centerTeflCosts?.data.filter((cost) => cost.numberOfTefl > 0) ?? [],
    [centerTeflCosts?.data]
  )

  const columns: EditableTableColumn<CenterTeflCost>[] = [
    ...getCenterTeflCostsColumns(costType, currency, t, centerTeflCostsEditableColumns[user.role]),
    ...(costType === 'final-cost'
      ? ([
          {
            accessorKey: 'invoices',
            header: t('center.costs.tefl.teachers.and.leaders.table.header.upload.invoices'),
            cell: ({ row }) => (
              <div className="flex flex-wrap gap-2 p-2">
                {row.original.invoices.length > 0
                  ? row.original.invoices.map((invoice) => (
                      <InvoiceButton
                        key={invoice.path}
                        variant={userInvoiceColorMapping[user.role]}
                        invoice={invoice}
                        readOnly={(!editable && mode === 'action-edit') || mode === 'readonly'}
                        disabled={isDisabled}
                        onDelete={(invoiceId) => {
                          if (!invoiceId) return
                          deleteCenterTeflCostsInvoiceMutation({
                            teflCostId: row.original.id,
                            invoiceId
                          })
                        }}
                      />
                    ))
                  : !editable && mode !== 'action-edit' && '-'}

                {(!editable && mode === 'action-edit') || mode === 'readonly' ? (
                  <span className="text-grey">-</span>
                ) : (
                  <UploadInvoicesButton
                    variant={userUploadInvoiceColorMapping[user.role]}
                    disabled={isDisabled}
                    types={[FileTypeEnum.PDF, FileTypeEnum.IMAGE]}
                    onChange={(files) => {
                      if (files.length > 0) {
                        uploadCenterTeflCostsInvoicesMutation({
                          teflCostId: row.original.id,
                          data: { file: files[0] }
                        })
                      }
                    }}
                  />
                )}
              </div>
            )
          }
        ] as EditableTableColumn<CenterTeflCost>[])
      : [])
  ]

  if (!centerTeflCosts || !data) return <CenterTableSkeleton />
  return (
    <div>
      <CenterCostsTableHeader
        mode={mode}
        icon={TeacherIcon}
        title={t('center.costs.tefl.teachers.and.leaders.title')}
        onCancel={() => setTeflCosts(undefined)}
        onEditableChange={setEditable}
        validateOptions={{
          validate: costType === 'final-cost',
          locked: data.isHostFamiliesLocked,
          onLock: lock.mutateAsync,
          onUnlock: unlock.mutateAsync
        }}
        onSave={async () => {
          if (teflCosts) {
            await mutateAsync(teflCosts)
            await Promise.all([refetch(), centerCostsView.refetch(), refetchCenter?.()])
          }
        }}
      />

      <EditableTable<CenterTeflCost>
        isEditable={editable}
        // display the tefl costs when we have a tefl assigned to the center
        data={displayedTeflCosts}
        columns={columns}
        onDataChange={(data) => {
          const mappedData = data.map((item) => mapCenterTeflCostToInput(item, costType))
          if (mode === 'free-edit') mutateAsync(mappedData)
          else setTeflCosts(mappedData)
        }}
        footerRowClassName={cn({ 'bg-white': costType === 'final-cost' })}
        footer={
          <CenterCostsTableFooter
            currency={currency}
            costType={costType}
            textColor={centerCostsTableColorMapping[user.role][costType].textColor}
            variant={centerCostsTableColorMapping[user.role][costType].variant}
            label={
              costType === 'final-cost'
                ? t('center.final.costs.fees')
                : t('center.costs.tefl.teachers.and.leaders.table.footer.total.host.family')
            }
            total={centerCostsView.data?.totalTeflCost}
            initialAmount={centerCostsView.data?.totalTeflCost}
            finalAmount={centerCostsView.data?.finalTotalTeflCost}
            balance={centerCostsView.data?.teflBalance}
          />
        }
      />
    </div>
  )
}
